/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as z from 'zod'

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

import { SettingsForm } from '../components/settings-form-layout'
import { SettingsPageFormActions } from '../components/settings-page-context'
import { SettingsSection } from '../components/settings-section'
import { useUpdateOption } from '../hooks/use-update-option'

const noticeSchema = z.object({
  Notice: z.string().optional(),
  MarqueeEnabled: z.boolean(),
  MarqueeContent: z.string().max(500),
})

type NoticeFormValues = z.infer<typeof noticeSchema>

type NoticeSectionProps = {
  defaultValues: {
    notice: string
    marqueeEnabled: boolean
    marqueeContent: string
  }
}

export function NoticeSection({ defaultValues }: NoticeSectionProps) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()
  const form = useForm<NoticeFormValues>({
    resolver: zodResolver(noticeSchema),
    defaultValues: {
      Notice: defaultValues.notice,
      MarqueeEnabled: defaultValues.marqueeEnabled,
      MarqueeContent: defaultValues.marqueeContent,
    },
  })

  useEffect(() => {
    form.reset({
      Notice: defaultValues.notice,
      MarqueeEnabled: defaultValues.marqueeEnabled,
      MarqueeContent: defaultValues.marqueeContent,
    })
  }, [defaultValues, form])

  const onSubmit = async (values: NoticeFormValues) => {
    const normalized = values.Notice ?? ''
    const updates: Array<{ key: string; value: string | boolean }> = []

    if (normalized !== defaultValues.notice) {
      updates.push({ key: 'Notice', value: normalized })
    }

    if (values.MarqueeEnabled !== defaultValues.marqueeEnabled) {
      updates.push({ key: 'MarqueeEnabled', value: values.MarqueeEnabled })
    }

    if (values.MarqueeContent !== defaultValues.marqueeContent) {
      updates.push({ key: 'MarqueeContent', value: values.MarqueeContent })
    }

    for (const update of updates) {
      await updateOption.mutateAsync(update)
    }
  }

  return (
    <SettingsSection title={t('System Notice')}>
      <Form {...form}>
        <SettingsForm onSubmit={form.handleSubmit(onSubmit)}>
          <SettingsPageFormActions
            onSave={form.handleSubmit(onSubmit)}
            isSaving={updateOption.isPending}
            saveLabel='Save notice'
          />
          <FormField
            control={form.control}
            name='Notice'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('Announcement content')}</FormLabel>
                <FormControl>
                  <Textarea
                    rows={8}
                    placeholder={t(
                      'Planned maintenance on Friday at 22:00 UTC...'
                    )}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='border-border border-t pt-5'>
            <h3 className='text-sm font-medium'>{t('Rolling announcement')}</h3>
            <p className='text-muted-foreground mt-1 text-sm'>
              {t(
                'Show a continuously scrolling announcement below the site header.'
              )}
            </p>
          </div>

          <FormField
            control={form.control}
            name='MarqueeEnabled'
            render={({ field }) => (
              <FormItem className='flex items-center justify-between gap-4'>
                <div className='space-y-1'>
                  <FormLabel>{t('Enable rolling announcement')}</FormLabel>
                  <FormDescription>
                    {t('Visitors can dismiss it on their current device.')}
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='MarqueeContent'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('Announcement content')}</FormLabel>
                <FormControl>
                  <Textarea
                    rows={3}
                    maxLength={500}
                    placeholder={t(
                      'Service updates, offers, or important reminders...'
                    )}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t('Displayed in a continuous ticker below the site header.')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </SettingsForm>
      </Form>
    </SettingsSection>
  )
}
